import { WIND_SHEETS, validateWindSheets, type WindSheetEntry } from "odyssey-shared";

/**
 * The wind art a page draws with (ADR-144's amendment of 2026-10-31), served as the projectile looks are: a pointer under the
 * asset base, `assets/weather/current.json`, lists each sheet with the hashed path of its image, and each sheet is a texture of
 * a grid of equal frames. The pointer and the sheets load once, before the join, and nothing here fails the page: a pointer that
 * does not answer or validate leaves the page with no wind art, and a sheet that does not load, or whose image is not its grid's
 * size, is left out, each with one warning. The view draws nothing for a sheet this does not hold.
 */

/**
 * Where the pointer is, under the asset base.
 */
export const WIND_POINTER = "assets/weather/current.json";

/**
 * What of the texture manager the sheets use.
 */
export interface SheetTextures {
  exists(key: string): boolean;
  addSpriteSheet(key: string, source: HTMLImageElement, config: { frameWidth: number; frameHeight: number }): unknown;
}

/**
 * How the sheets reach the network: the pointer's fetch, an image's load and the warning, each the page's own unless a test
 * hands in another.
 */
export interface SheetSources {
  readonly fetcher: (url: string, init: RequestInit) => Promise<Response>;
  readonly image: (url: string) => Promise<HTMLImageElement>;
  readonly warn: (line: string) => void;
}

/**
 * Loads an image and decodes it before it is handed on, so its upload does not decode it in a frame.
 *
 * @param url The image's address.
 * @returns The decoded image.
 * @throws Error when it does not load or decode.
 */
async function loadImage(url: string): Promise<HTMLImageElement> {
  const image = new Image();
  image.crossOrigin = "anonymous";
  image.src = url;
  await image.decode();
  return image;
}

const PAGE_SOURCES: SheetSources = { fetcher: (url, init) => fetch(url, init), image: loadImage, warn: (line) => console.warn(line) };

/**
 * The texture key of a sheet. It carries the sheet's path, which carries the hash of its bytes, so a page that boots a world
 * again without a reload makes a texture for a sheet that has changed.
 *
 * @param path The sheet's path under the asset base.
 * @returns The key.
 */
export function windTextureKey(path: string): string {
  return `weather:${path}`;
}

/**
 * The wind sheets of one page.
 */
export class WindSheets {
  private readonly keys = new Map<string, string>();
  private asked: Promise<void> | null = null;

  /**
   * The texture key of a sheet.
   *
   * @param id The sheet's id (`WIND_SHEETS`).
   * @returns The key, or null when the page holds no such sheet.
   */
  public keyOf(id: string): string | null {
    return this.keys.get(id) ?? null;
  }

  /**
   * The ids of the sheets held.
   *
   * @returns The ids.
   */
  public ids(): string[] {
    return [...this.keys.keys()];
  }

  /**
   * Loads the pointer and every sheet. Asked again, it hands back the first load. It never rejects.
   *
   * @param textures Where the sheets become textures.
   * @param base The asset base the page loads from.
   * @param sources The fetch, the image load and the warning; the page's own unless given.
   * @returns A promise that settles once every sheet is held or left out.
   */
  public start(textures: SheetTextures, base: string, sources: SheetSources = PAGE_SOURCES): Promise<void> {
    this.asked ??= this.load(textures, base, sources);
    return this.asked;
  }

  /**
   * Reads the pointer and loads each sheet.
   *
   * @param textures Where the sheets become textures.
   * @param base The asset base.
   * @param sources The fetch, the image load and the warning.
   */
  private async load(textures: SheetTextures, base: string, sources: SheetSources): Promise<void> {
    const url = `${base}/${WIND_POINTER}`;
    let entries: readonly WindSheetEntry[];
    try {
      const response = await sources.fetcher(url, { cache: "no-cache" });
      if (!response.ok) throw new Error(`responded ${response.status}`);
      entries = Object.values(validateWindSheets(await response.json()).sheets);
    } catch (error) {
      sources.warn(`no wind is drawn this session: ${url} ${error instanceof Error ? error.message : String(error)}`);
      return;
    }
    const loaded = await Promise.all(entries.map((entry) => this.loadSheet(textures, base, entry, sources)));
    for (const entry of loaded) {
      if (entry !== null) this.keys.set(entry.id, windTextureKey(entry.path));
    }
  }

  /**
   * Loads one sheet into a texture.
   *
   * @param textures Where the sheet becomes a texture.
   * @param base The asset base.
   * @param entry The sheet.
   * @param sources The image load and the warning.
   * @returns The entry when the sheet is held, else null, with one warning.
   */
  private async loadSheet(textures: SheetTextures, base: string, entry: WindSheetEntry, sources: SheetSources): Promise<WindSheetEntry | null> {
    const key = windTextureKey(entry.path);
    try {
      if (textures.exists(key)) return entry;
      const image = await sources.image(`${base}/${entry.path}`);
      const rows = Math.ceil(entry.frames / entry.columns);
      if (image.width !== entry.frameWidth * entry.columns || image.height !== entry.frameHeight * rows) {
        throw new Error(`${entry.path} is ${image.width} by ${image.height}, not ${entry.columns} by ${rows} frames of ${entry.frameWidth} by ${entry.frameHeight}`);
      }
      if (!textures.exists(key) && textures.addSpriteSheet(key, image, { frameWidth: entry.frameWidth, frameHeight: entry.frameHeight }) === null) {
        throw new Error(`${entry.path} did not become a texture`);
      }
      return entry;
    } catch (error) {
      sources.warn(`wind sheet ${entry.id} is not drawn this session: ${error instanceof Error ? error.message : String(error)}`);
      return null;
    }
  }
}

/**
 * The ids of every sheet the presets name, for a caller that needs to know what a complete load holds.
 */
export const WIND_SHEET_IDS: readonly string[] = WIND_SHEETS.map((sheet) => sheet.id);
